import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { BLOG } from 'src/app/data/blog/blog.data';
import { URLS } from 'src/app/data/navigation/navigation.data';
import { THEMES } from 'src/app/data/product/product.data';
import { environmentCommon } from 'src/environments/environment-common';
import { environment } from 'src/environments/environment';
import { Blog } from 'src/app/model/blog/blog.model';
import { Product, isThemeFree } from 'src/app/model/product/product.model';
import { SeoService } from 'src/app/service/seo/seo.service';
import { AnalyticsService } from 'src/app/service/analytics/analytics.service';
import { DeckShot } from 'src/app/component/blog/screenshot-deck/screenshot-deck.component';

// One piece of the post body: a block of pre-rendered HTML, or a live widget
// (`widget` = its name in WIDGETS; `shots` only for the screenshot deck).
interface BodySegment {
  html?: SafeHtml;
  widget?: string;
  shots?: DeckShot[];
}

interface RelatedBox {
  label: string;
  // Internal target (router) OR external href.
  routerLink?: string;
  queryParams?: { [ key: string ]: string };
  href?: string;
  external?: boolean;
  // Visuals: an optional background image (themes), an icon image, and/or a
  // color/gradient swatch.
  bgImage?: string;
  iconImg?: string;
  swatch?: string;
  // GitHub mark: theme-aware (black on light, inverted to white on dark).
  github?: boolean;
  // Tool artwork (color/palette): shown full, rounded-rectangle, not a circle.
  tool?: boolean;
  // Theme logo: circle, no padding, image fills the circle (object-fit cover).
  themeIcon?: boolean;
  // For theme boxes: the theme's OWN primary colour, used for the label (so the
  // box speaks in the theme's identity, not the site's theme color).
  accentColor?: string;
}

const PLATFORM_LABELS: { [ key: string ]: string } = {
  jetbrains: 'JetBrains',
  vscode: 'VS Code',
  cursor: 'Cursor',
  antigravity: 'Antigravity',
  chrome: 'Chrome',
  firefox: 'Firefox',
  slack: 'Slack',
  ghostty: 'Ghostty',
  iterm2: 'iTerm2',
  macos: 'macOS',
};

const GITHUB_BASE = environmentCommon.website.github.url + '/' + environmentCommon.company.social.github;

// The exact-match converter pages a post can point to via `relatedConverters`;
// each box uses the converter's own gradient-badge icon.
const CONVERTER_BOXES: { [ slug: string ]: { label: string; url: string } } = {
  'hex-to-rgb': { label: 'HEX to RGB', url: URLS.tools.hexToRgb },
  'rgb-to-hex': { label: 'RGB to HEX', url: URLS.tools.rgbToHex },
  'hex-to-hsl': { label: 'HEX to HSL', url: URLS.tools.hexToHsl },
  'hsl-to-hex': { label: 'HSL to HEX', url: URLS.tools.hslToHex },
  'hex-to-hsv': { label: 'HEX to HSV', url: URLS.tools.hexToHsv },
  'hsv-to-hex': { label: 'HSV to HEX', url: URLS.tools.hsvToHex },
  'hex-to-hsb': { label: 'HEX to HSB', url: URLS.tools.hexToHsb },
  'hsb-to-hex': { label: 'HSB to HEX', url: URLS.tools.hsbToHex },
  'hex-to-hwb': { label: 'HEX to HWB', url: URLS.tools.hexToHwb },
  'hwb-to-hex': { label: 'HWB to HEX', url: URLS.tools.hwbToHex },
  'hex-to-cmyk': { label: 'HEX to CMYK', url: URLS.tools.hexToCmyk },
  'cmyk-to-hex': { label: 'CMYK to HEX', url: URLS.tools.cmykToHex },
  'hex-to-oklch': { label: 'HEX to OKLCH', url: URLS.tools.hexToOklch },
  'oklch-to-hex': { label: 'OKLCH to HEX', url: URLS.tools.oklchToHex },
  'hex-to-oklab': { label: 'HEX to OKLAB', url: URLS.tools.hexToOklab },
  'oklab-to-hex': { label: 'OKLAB to HEX', url: URLS.tools.oklabToHex },
  'hex-to-cielab': { label: 'HEX to CIELAB', url: URLS.tools.hexToCielab },
  'cielab-to-hex': { label: 'CIELAB to HEX', url: URLS.tools.cielabToHex },
  'hex-to-cielch': { label: 'HEX to CIELCH', url: URLS.tools.hexToCielch },
  'cielch-to-hex': { label: 'CIELCH to HEX', url: URLS.tools.cielchToHex },
  'hex-to-cieluv': { label: 'HEX to CIELUV', url: URLS.tools.hexToCieluv },
  'cieluv-to-hex': { label: 'CIELUV to HEX', url: URLS.tools.cieluvToHex },
  'hex-to-display-p3': { label: 'HEX to Display P3', url: URLS.tools.hexToDisplayP3 },
  'display-p3-to-hex': { label: 'Display P3 to HEX', url: URLS.tools.displayP3ToHex },
  'hex-to-xyz': { label: 'HEX to XYZ', url: URLS.tools.hexToXyz },
  'xyz-to-hex': { label: 'XYZ to HEX', url: URLS.tools.xyzToHex },
  'hex-to-yxy': { label: 'HEX to Yxy', url: URLS.tools.hexToYxy },
  'yxy-to-hex': { label: 'Yxy to HEX', url: URLS.tools.yxyToHex },
  'hex-to-hunter-lab': { label: 'HEX to Hunter Lab', url: URLS.tools.hexToHunterLab },
  'hunter-lab-to-hex': { label: 'Hunter Lab to HEX', url: URLS.tools.hunterLabToHex },
};

// A post never renders more than this many Keep Exploring boxes.
// every post shows exactly four (owner 2026-10-12): its own picks first, then the color tools fill the rest
const MAX_RELATED_BOXES = 4;

@Component( {
  selector: 'app-blog-post-page',
  templateUrl: './blog-post-page.component.html',
  styleUrls: [
    '../../tool-pages/tools-page/tools-page.component.scss',
    '../../plugin-pages/plugins-page/plugins-page.component.scss',
    './blog-post-page.component.scss',
  ],
} )
export class BlogPostPageComponent implements OnInit, OnDestroy {

  URLS = URLS;

  post: Blog | null = null;
  // The body as a run of HTML segments and live widgets. A post can embed several
  // widgets: each <!--X-START-->...<!--X-END--> marker becomes one widget segment
  // and the HTML around it plain segments. The static fallback between the markers
  // stays in the .html fragment (and the server <noscript>) for crawlers / no-JS
  // readers, and is dropped here (the screenshot deck reads its shots from it).
  segments: BodySegment[] = [];
  widgetHex = '#00BAFF';
  // The opposites widget pairs the harmony-preview (left) with the color wheel
  // (right); the preview owns the colour and feeds it to the wheel via this.
  oppositesHex = '#00BAFF';
  relatedBoxes: RelatedBox[] = [];
  relatedTitle = '';
  // "Premium Themes With A Similar Palette": the post's build-time premium picks (see
  // Blog.premiumThemes). Empty for most posts, and then the section is not rendered.
  premiumBoxes: RelatedBox[] = [];
  // The free theme the picks are close to, named in the section's lead line.
  premiumAnchorName = '';

  private sub?: Subscription;
  // The post whose premium block was already reported as seen, and the observer
  // watching for it (Premium Suggestions Shown fires once per post, on sight).
  private premiumSeenFor = '';
  private premiumObserver?: IntersectionObserver;

  // The premium section (inside an *ngIf, so it comes and goes with the post).
  // Reported only once at least half of it has been on screen: the event is the
  // denominator of the block's click rate, and a post view is not a sighting.
  @ViewChild( 'premiumSection' ) set premiumSection( ref: ElementRef<HTMLElement> | undefined ) {
    this.premiumObserver?.disconnect();
    this.premiumObserver = undefined;
    if ( !ref || typeof IntersectionObserver === 'undefined' ) {
      return;
    }
    this.premiumObserver = new IntersectionObserver( entries => {
      if ( entries.some( e => e.isIntersecting ) ) {
        this.reportPremiumSeen();
      }
    }, { threshold: 0.5 } );
    this.premiumObserver.observe( ref.nativeElement );
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private sanitizer: DomSanitizer,
    private seoService: SeoService,
    private analyticsService: AnalyticsService,
  ) {
  }

  ngOnInit(): void {
    this.sub = this.route.paramMap.subscribe( params => {
      const slug = params.get( 'slug' );
      const post = BLOG.find( p => p.key === slug ) || null;
      if ( !post ) {
        this.router.navigateByUrl( URLS.blog );
        return;
      }
      this.post = post;
      this.relatedBoxes = this.buildRelated( post );
      this.relatedTitle = this.buildRelatedTitle( post );
      this.premiumBoxes = this.buildPremium( post );
      this.premiumAnchorName = this.findTheme( ( post.relatedThemes || [] )[ 0 ] || '' )?.product?.name || '';
      this.applySeo( post );
      this.loadBody( post.key );
      this.analyticsService.logEvent( 'Blog Post Viewed', {
        post: post.key,
        title: post.title,
        category: post.category,
      } );
      this.premiumSeenFor = '';
    } );
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.premiumObserver?.disconnect();
  }

  private reportPremiumSeen(): void {
    const key = this.post?.key || '';
    if ( !key || !this.premiumBoxes.length || this.premiumSeenFor === key ) {
      return;
    }
    this.premiumSeenFor = key;
    this.analyticsService.logEvent( 'Premium Suggestions Shown', {
      post: key,
      // Capitalised the way AnalyticsService capitalises a single value, so these
      // read the same as Theme in the Clicked, Theme Viewed and Cart events.
      themes: this.premiumBoxes.map( box => {
        const k = box.queryParams?.product || '';
        return k.charAt( 0 ).toUpperCase() + k.slice( 1 );
      } ),
    } );
  }

  // A "Keep Exploring" box was clicked. Navigation still happens via the
  // template's routerLink/href; this only records what kind of target it was.
  onRelatedBoxClick( box: RelatedBox ): void {
    const kind = box.github ? 'repo'
      : box.themeIcon ? 'theme'
      : box.tool ? 'tool'
      : box.routerLink === URLS.themes ? 'platform'
      : 'other';
    const themeKey = box.themeIcon ? box.queryParams?.product || '' : '';
    const theme = themeKey ? this.findTheme( themeKey )?.product : null;
    this.analyticsService.logEvent( 'Related Box Clicked', {
      kind,
      label: box.label,
      target: box.href || box.routerLink || '',
      post: this.post?.key || '',
      // Theme boxes: which theme, and whether it is a paid one (Mixpanel only).
      ...( theme ? { theme: themeKey, paid: !isThemeFree( theme ) } : {} ),
    } );
  }

  // A "Premium Themes With A Similar Palette" box was clicked. Navigation happens via
  // the routerLink; this records which pick, and where it sat, for Mixpanel.
  onPremiumBoxClick( box: RelatedBox, position: number ): void {
    this.analyticsService.logEvent( 'Premium Suggestion Clicked', {
      post: this.post?.key || '',
      theme: box.queryParams?.product || '',
      position: position + 1,
      of: this.premiumBoxes.length,
    } );
  }

  // Prev ("Newer") / next ("Older") footer navigation.
  onPostNavClick( direction: 'newer' | 'older' ): void {
    this.analyticsService.logEvent( 'Blog Post Nav', {
      direction,
      from: this.post?.key || '',
    } );
  }

  // Build the small "related" boxes: the targets the post set, then the color tools up to four.
  private buildRelated( post: Blog ): RelatedBox[] {
    const boxes: RelatedBox[] = [];

    for ( const key of post.relatedThemes || [] ) {
      const match = this.findTheme( key );
      const theme = match?.product;
      // The product alone: the theme page derives the category from the product's
      // line, and the canonical of /theme keeps only `product`, so this link points
      // at the indexed URL instead of a variant that canonicalises elsewhere.
      boxes.push( {
        label: theme ? theme.name : this.titleCase( key ),
        routerLink: URLS.theme,
        queryParams: { product: key.toLowerCase() },
        iconImg: theme?.logo,
        themeIcon: true,
        accentColor: theme?.designTokens?.primaryColor || theme?.color,
      } );
    }

    if ( post.relatedPlatform ) {
      const p = post.relatedPlatform;
      boxes.push( {
        label: PLATFORM_LABELS[ p ] || this.titleCase( p ),
        routerLink: URLS.themes,
        queryParams: { platform: p },
        iconImg: `/assets/third-parties/logo/${ p }.webp`,
      } );
    }

    // Color-theory / palette posts link to the GENERAL tool page, using the
    // existing tool artwork as the icon.
    // Exact-match converters rank ABOVE the generic color / palette boxes:
    // when the 4-box cap slices, the specific tools win (too many posts were
    // showing the same Color Tool + Palette Generator pair).
    for ( const slug of post.relatedConverters || [] ) {
      const conv = CONVERTER_BOXES[ slug ];
      if ( !conv ) { continue; }
      boxes.push( {
        label: conv.label,
        routerLink: conv.url,
        iconImg: `/assets/product-lines/tools/${ slug }.svg`,
        tool: true,
      } );
    }

    if ( post.relatedColor ) {
      boxes.push( {
        label: 'Color Explorer',
        routerLink: URLS.tools.color,
        iconImg: '/assets/product-lines/tools/color-card.svg',
        tool: true,
      } );
    }

    if ( post.relatedPalette ) {
      boxes.push( {
        label: 'Palette Generator',
        routerLink: URLS.tools.palette,
        iconImg: '/assets/product-lines/tools/color-palette.svg',
        tool: true,
      } );
    }

    if ( post.relatedGradient ) {
      boxes.push( {
        label: 'Gradient Generator',
        routerLink: URLS.tools.gradient,
        iconImg: '/assets/product-lines/tools/gradient-card.svg',
        tool: true,
      } );
    }

    if ( post.relatedRepo ) {
      boxes.push( {
        label: post.relatedRepo,
        href: GITHUB_BASE + '/' + post.relatedRepo,
        external: true,
        iconImg: '/assets/third-parties/logo/github.webp',
        github: true,
      } );
    }

    // fill up to four with the color tools the post has not named yet
    const fillers: RelatedBox[] = [
      { label: 'Color Explorer', routerLink: URLS.tools.color, iconImg: '/assets/product-lines/tools/color-card.svg', tool: true },
      { label: 'Palette Generator', routerLink: URLS.tools.palette, iconImg: '/assets/product-lines/tools/color-palette.svg', tool: true },
      { label: 'Gradient Generator', routerLink: URLS.tools.gradient, iconImg: '/assets/product-lines/tools/gradient-card.svg', tool: true },
      { label: 'Wallpaper Generator', routerLink: URLS.tools.wallpaper, iconImg: '/assets/product-lines/tools/wallpaper-card.png', tool: true },
    ];
    for ( const filler of fillers ) {
      if ( boxes.length >= MAX_RELATED_BOXES ) { break; }
      if ( !boxes.some( b => b.label === filler.label ) ) { boxes.push( filler ); }
    }

    return boxes.slice( 0, MAX_RELATED_BOXES );
  }

  // The premium picks as boxes, in the Keep Exploring design. A pick that is not
  // in the catalogue, or no longer premium, is dropped here as well, so a stale
  // build never shows a free theme under a "Premium" heading.
  private buildPremium( post: Blog ): RelatedBox[] {
    const boxes: RelatedBox[] = [];
    for ( const key of post.premiumThemes || [] ) {
      const theme = this.findTheme( key )?.product;
      if ( !theme || isThemeFree( theme ) ) {
        continue;
      }
      boxes.push( {
        label: theme.name,
        routerLink: URLS.theme,
        queryParams: { product: theme.key },
        iconImg: theme.logo,
        themeIcon: true,
        accentColor: theme.designTokens?.primaryColor || theme.color,
      } );
    }
    return boxes.slice( 0, 2 );
  }

  // One shared heading for the related box, whatever it holds (themes, a
  // platform, tools, a repo) - so the section never splits into per-kind
  // titles like "Related Themes" vs "Related Tools".
  private buildRelatedTitle( post: Blog ): string {
    return 'Keep Exploring';
  }

  private findTheme( key: string ): { product: Product; category: string } | null {
    for ( const line of THEMES ) {
      const found = line.products.find( p => p.key === key );
      if ( found ) {
        return { product: found, category: line.name };
      }
    }
    return null;
  }

  private applySeo( post: Blog ): void {
    this.seoService.setMetaData( {
      // Same title the server ships (blogPostSeo), so crawlers that render JS and those
      // that do not see one title.
      title: post.title + ' | Codigrate Blog',
      description: post.excerpt,
      keywords: [ ...post.tags, post.categoryLabel, 'codigrate', 'blog' ],
      robots: [ 'index', 'follow', 'max-image-preview:large', 'max-snippet:-1' ],
    } );
  }

  // The rendered body lives as a static fragment so the JS bundle stays small
  // and grows with post count only on disk, not in the SPA. A native fetch
  // bypasses the HTTP interceptors (which target the API), and the same file is
  // injected server-side into <noscript> for crawlers (see seo-route-meta.js).
  private loadBody( slug: string ): void {
    this.segments = [];
    fetch( `/assets/blog/content/${ slug }.html` )
      .then( res => ( res.ok ? res.text() : '' ) )
      .then( html => this.applyBody( html ) )
      .catch( () => {
        this.segments = [];
      } );
  }

  // Embedded widget markers: a post wraps a static fallback in one of these and the
  // matching live component is rendered in its place (the fallback stays in the
  // .html fragment + server <noscript> for crawlers / no-JS readers).
  private static readonly WIDGETS: { name: string, start: string, end: string }[] = [
    { name: 'color-converter', start: '<!--CC-START-->', end: '<!--CC-END-->' },
    { name: 'color-harmony', start: '<!--HARMONY-START-->', end: '<!--HARMONY-END-->' },
    { name: 'readability', start: '<!--RB-START-->', end: '<!--RB-END-->' },
    { name: 'opposites', start: '<!--OPP-START-->', end: '<!--OPP-END-->' },
    { name: 'light-mixer', start: '<!--LIGHT-START-->', end: '<!--LIGHT-END-->' },
    { name: 'color-wheel', start: '<!--WHEEL-START-->', end: '<!--WHEEL-END-->' },
    { name: 'temperature', start: '<!--TEMP-START-->', end: '<!--TEMP-END-->' },
    { name: 'theme-palette', start: '<!--THEME-START-->', end: '<!--THEME-END-->' },
    { name: 'color-blindness', start: '<!--CVD-START-->', end: '<!--CVD-END-->' },
    { name: 'saturation-wheel', start: '<!--SAT-START-->', end: '<!--SAT-END-->' },
    { name: 'value-ladder', start: '<!--VAL-START-->', end: '<!--VAL-END-->' },
    { name: 'value-rise', start: '<!--HSV-START-->', end: '<!--HSV-END-->' },
    { name: 'gradient-preview', start: '<!--GRAD-START-->', end: '<!--GRAD-END-->' },
    { name: 'mode-room', start: '<!--MODE-START-->', end: '<!--MODE-END-->' },
    { name: 'hwb-mixer', start: '<!--HWB-START-->', end: '<!--HWB-END-->' },
    { name: 'cmyk-mixer', start: '<!--CMYK-START-->', end: '<!--CMYK-END-->' },
    { name: 'screenshot-deck', start: '<!--SHOTS-START-->', end: '<!--SHOTS-END-->' },
  ];

  // Splits the body around every widget marker, in order, so each live widget is
  // rendered in place of its static fallback. A body without markers is one segment.
  private applyBody( html: string ): void {
    const segments: BodySegment[] = [];
    let rest = html || '';
    while ( rest ) {
      let widget: { name: string, start: string, end: string } | null = null;
      let at = -1;
      for ( const w of BlogPostPageComponent.WIDGETS ) {
        const start = rest.indexOf( w.start );
        if ( start !== -1 && ( at === -1 || start < at ) && rest.indexOf( w.end, start ) !== -1 ) {
          widget = w;
          at = start;
        }
      }
      if ( !widget ) {
        segments.push( { html: this.sanitizer.bypassSecurityTrustHtml( rest ) } );
        break;
      }
      const end = rest.indexOf( widget.end, at );
      if ( at > 0 ) {
        segments.push( { html: this.sanitizer.bypassSecurityTrustHtml( rest.slice( 0, at ) ) } );
      }
      const inner = rest.slice( at + widget.start.length, end );
      segments.push( {
        widget: widget.name,
        shots: widget.name === 'screenshot-deck' ? this.readShots( inner ) : undefined,
      } );
      rest = rest.slice( end + widget.end.length );
    }
    this.segments = segments;
  }

  // The shots of a screenshot deck, read from the <figure> list its marker wraps.
  private readShots( html: string ): DeckShot[] {
    const shots: DeckShot[] = [];
    const re = /<figure[^>]*>[\s\S]*?<img[^>]*\ssrc="([^"]+)"[^>]*\salt="([^"]*)"[\s\S]*?<figcaption>([\s\S]*?)<\/figcaption>[\s\S]*?<\/figure>/g;
    let m = re.exec( html );
    while ( m ) {
      shots.push( { src: m[ 1 ], alt: m[ 2 ], caption: m[ 3 ].trim() } );
      m = re.exec( html );
    }
    return shots;
  }

  private titleCase( slug: string ): string {
    return slug
      .replace( /[-_]+/g, ' ' )
      .replace( /\b\w/g, c => c.toUpperCase() );
  }
}
